import { useCallback, useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import GuidanceModal from 'growth-onboarding-empty-states/GuidanceModal/GuidanceModal';
import DoItMyselfButton from 'growth-onboarding-empty-states/GuidanceModal/buttons/DoItMyselfButton';
import HelpMeGetStartedButton from 'growth-onboarding-empty-states/GuidanceModal/buttons/HelpMeGetStartedButton';
import FormattedMessage from 'I18n/components/FormattedHTMLMessage';
import UsageTracker, { trackUserInteraction } from 'MeetingsAdmin/tracking/UsageTracker';
// @ts-expect-error untyped import
import NotificationList from 'ReduxMessenger/components/NotificationList';
// @ts-expect-error untyped import
import { setupCsat } from 'wootric-nps/csat/csat-setup';
import { useIsFreeOrStarterPortal } from 'onboarding-scopes';
import { getMeetingsSettingsPathname } from 'MeetingsAdmin/utils/utils';
// @ts-expect-error untyped import
import { fetchMeetingsUser } from '../redux/actions/MeetingsUserActions';
// @ts-expect-error untyped import
import * as RequestStatusSelector from '../selectors/RequestStatusSelector';
import { getIsJitaUser, getUngatedForCentralizedCalendarSync
// @ts-expect-error untyped import
} from '../selectors/AccessSelector';
import MeetingsUserZeroState from './MeetingsUserZeroState';
import MeetingsUserEmptyState from '../components/MeetingsUserEmptyState';
// @ts-expect-error untyped import
import { fetchUserAttributes } from 'MeetingsAdmin/redux/actions/UserAttributesActions';
import { getCentralizedCalendarConnected as getCentralizedCalendarConnectedSelector } from 'MeetingsAdmin/selectors/CentralizedCalendarSelectors';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import OnboardingTour from 'onboarding-tours/components/OnboardingTour';
import { isTourCurrentlyActive } from 'onboarding-tours/util/activeTour';
import { subscribeToTourCompletion, subscribeToTourClose } from 'onboarding-tours/util/tourEvents';
import { useIsFirstPageVisit } from '../hooks/useIsFirstPageVisit';
// @ts-expect-error untyped import
import { getUserId } from '../selectors/AuthSelector';
import { useMeetingsTour } from '../hooks/useMeetingsTour';
import { useCreateSalesExtensionDownloadModal } from '../hooks/createSalesExtensionModal';
import Raven from 'raven-js';
import { View } from 'foundations-components/components/View';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function AdminContainer({
  children
}) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    createGrowthPrompt
  } = useCreateSalesExtensionDownloadModal();
  const [shouldReloadWindow, setShouldReloadWindow] = useState(false);
  const isLoadingMeetingsUser = useSelector(state => RequestStatusSelector.isLoading(state));
  const meetingsUserFetchFailed = useSelector(state => RequestStatusSelector.meetingsUserFetchFailed(state));
  const isCentralizedCalendarConnected = useSelector(getCentralizedCalendarConnectedSelector);
  const userId = useSelector(getUserId);
  const isJitaUser = useSelector(state => getIsJitaUser(state));
  const isEmptyState = meetingsUserFetchFailed && !isJitaUser;
  const isUngatedForCentralizedCalendarSync = useSelector(state => getUngatedForCentralizedCalendarSync(state));
  const isStarterOrFreePortal = useIsFreeOrStarterPortal();
  const isFirstTimeVisitingPage = useIsFirstPageVisit();
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [hasSeenWelcomeModal, setHasSeenWelcomeModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const {
    onboardingTourId,
    setIsGuidedOnboarding
  } = useMeetingsTour();
  const meetingLinkCreatedViaCalendarSync = isCentralizedCalendarConnected && isFirstTimeVisitingPage;
  const isTourActive = isTourCurrentlyActive();
  const routeToIndex = useCallback(() => {
    history.push('/');
  }, [history]);
  const routeToOneSettings = useCallback(() => {
    const url = getMeetingsSettingsPathname();
    routeToIndex();
    window.location.pathname = url;
  }, [routeToIndex]);
  useEffect(() => {
    // We show empty state in following cases
    // - user has no meeting link
    // - free/Starter user has meeting link created in background via calendar sync (but do not show when user started empty state flow already)
    const shouldShowEmptyState = isEmptyState || isStarterOrFreePortal && meetingLinkCreatedViaCalendarSync && !hasSeenWelcomeModal;
    setShowEmptyState(shouldShowEmptyState);
  }, [isEmptyState, hasSeenWelcomeModal, meetingLinkCreatedViaCalendarSync, isStarterOrFreePortal]);
  useEffect(() => {
    dispatch(fetchMeetingsUser(false, isUngatedForCentralizedCalendarSync));
    dispatch(fetchUserAttributes());
  }, [dispatch, isUngatedForCentralizedCalendarSync]);
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: 'admin'
    });
    const currentPath = location.pathname;
    if (currentPath.includes('setup')) {
      routeToIndex();
    } else if (currentPath.includes('settings')) {
      routeToOneSettings();
    }
  }, [location, routeToIndex, routeToOneSettings]);
  setupCsat('PROSPECTING_MEETINGS');
  useEffect(() => {
    const unsubscribeToTourCompletion = subscribeToTourCompletion(tourId => {
      if (tourId === onboardingTourId) {
        if (shouldReloadWindow) {
          window.location.reload();
        }
      }
    });
    const unsubscribeToClose = subscribeToTourClose(tourId => {
      if (tourId === onboardingTourId) {
        if (shouldReloadWindow) {
          window.location.reload();
        }
      }
    });
    return () => {
      unsubscribeToTourCompletion();
      unsubscribeToClose();
    };
  }, [onboardingTourId, userId, shouldReloadWindow]);
  const handleWelcomeModalCTAClick = () => {
    setIsGuidedOnboarding(true);
    trackUserInteraction('click-help-me-get-started');
    setShowWelcomeModal(false);
  };
  const handleWelcomeModalManualSetupClick = () => {
    trackUserInteraction('click-i-will-setup-myself');
    setShowWelcomeModal(false);
    if (shouldReloadWindow) {
      window.location.reload();
    }
  };
  const handleWelcomeModalClose = () => {
    trackUserInteraction('close-button-clicked');
    setShowWelcomeModal(false);
    if (shouldReloadWindow) {
      window.location.reload();
    }
  };
  const renderEmptyState = () => {
    return isStarterOrFreePortal ? /*#__PURE__*/_jsx(MeetingsUserEmptyState, {
      hideEmptyState: () => setShowEmptyState(false),
      setShowWelcomeModal: () => {
        createGrowthPrompt().then(data => {
          setShouldReloadWindow(data.isGrowthModalCreated);
        }).catch(error => {
          Raven.captureException(error, {
            extra: {
              message: 'Error when creating Sales extension growth prompt'
            }
          });
        });
        if (!isTourActive) {
          setHasSeenWelcomeModal(true);
          setShowWelcomeModal(true);
        }
      },
      meetingLinkCreated: meetingLinkCreatedViaCalendarSync
    }) : /*#__PURE__*/_jsx(MeetingsUserZeroState, {});
  };
  if (isLoadingMeetingsUser) {
    return /*#__PURE__*/_jsx(View, {
      blockSize: "100%",
      children: /*#__PURE__*/_jsx(LoadingDots, {
        minHeight: 400
      })
    });
  }

  // We should not show tour for selenium tests as it will cause multiple tour instances to be created
  const isSeleniumTestForTour = sessionStorage.getItem('tour-selenium-test');

  // check if a tour is active
  return /*#__PURE__*/_jsxs(View, {
    blockSize: "100%",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "MEETINGS_CONTAINER_LOAD"
    }), !isSeleniumTestForTour ? /*#__PURE__*/_jsx(OnboardingTour, {
      tourId: onboardingTourId
    }) : null, /*#__PURE__*/_jsx(NotificationList, {}), /*#__PURE__*/_jsx(FloatingAlertList, {}), showEmptyState ? renderEmptyState() : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isStarterOrFreePortal && showWelcomeModal && /*#__PURE__*/_jsxs(GuidanceModal, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "meetingsUserEmptyState.welcomeModal.title"
        }),
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "meetingsUserEmptyState.welcomeModal.subTitle"
        }),
        onClose: handleWelcomeModalClose,
        children: [/*#__PURE__*/_jsx(HelpMeGetStartedButton, {
          "data-test-id": "empty-state-guidance-guided-setup-button",
          onClick: event => {
            event.preventDefault();
            handleWelcomeModalCTAClick();
          }
        }), /*#__PURE__*/_jsx(DoItMyselfButton, {
          "data-test-id": "empty-state-guidance-manual-setup-button",
          onClick: event => {
            event.preventDefault();
            handleWelcomeModalManualSetupClick();
          }
        })]
      }), children]
    })]
  });
}
export default AdminContainer;